/**
 * RelOverviewTab - KPIs for a relationship: evidence count, sentiment summary.
 */

import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import ValenceBar from '../Charts/ValenceBar';
import { getConnectionStrength } from '../../utils/networkUtils';

function RelOverviewTab({ edge }) {
  const evidenceCount = edge.evidence_count || edge.weight || 0;
  const strength = getConnectionStrength(evidenceCount);

  const strengthColors = {
    strong: '#23A38F',
    medium: '#0097B2',
    weak: '#D8A200',
  };

  // Sentiment from edge data if available
  const positive = edge.positive_count || 0;
  const negative = edge.negative_count || 0;
  const neutral = edge.neutral_count || Math.max(0, evidenceCount - positive - negative);

  return (
    <Box sx={{ p: 2 }}>
      {/* Relationship Strength */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Relationship Strength
      </Typography>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: `${strengthColors[strength]}15`,
          border: 1,
          borderColor: strengthColors[strength],
          mb: 2,
        }}
      >
        <TrendingUpIcon sx={{ color: strengthColors[strength], fontSize: 20 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: strengthColors[strength], textTransform: 'capitalize' }}
        >
          {strength}
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        <MetricBox
          icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
          label="Evidence Count"
          value={evidenceCount}
        />
        <MetricBox
          icon={<CalendarIcon sx={{ fontSize: 18 }} />}
          label="First Co-mention"
          value={edge.first_seen ? new Date(edge.first_seen).toLocaleDateString() : 'N/A'}
        />
        <MetricBox
          icon={<CalendarIcon sx={{ fontSize: 18 }} />}
          label="Last Co-mention"
          value={edge.last_seen ? new Date(edge.last_seen).toLocaleDateString() : 'N/A'}
        />
        <MetricBox
          icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
          label="Relationship Type"
          value={edge.relationship_type || 'Co-mentioned'}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Sentiment Distribution */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Sentiment Distribution
      </Typography>
      {evidenceCount > 0 ? (
        <ValenceBar positive={positive} neutral={neutral} negative={negative} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No sentiment data available
        </Typography>
      )}
    </Box>
  );
}

function MetricBox({ icon, label, value }) {
  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.secondary' }}>
        {icon}
        <Typography variant="caption">{label}</Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default RelOverviewTab;
